import React, { useEffect, useRef, useState } from "react";
import useDimensions from "react-cool-dimensions";
import styles from "./mythImageEditor.module.css";
import { Corners } from "./comps/Corners";

import {
  loadImage,
  createOrientatedCanvas,
  getUnwarpedCanvas,
} from "./helpers";

export default function MythImageEditor({
  sourceImgUrl,
  onImageUpdate,
  editData,
  onCancel,
}) {
  const [unwarpedCanvas, setUnwarpedCanvas] = useState(null);
  const [imgCanvas, setImgCanvas] = useState(null);
  const [sourceCanvas, setSourceCanvas] = useState(null);
  const [cornerCoords, setCornerCoords] = useState(editData.cornerCoords);
  const [srcCanvasWidth, setSrcCanvasWidth] = useState(null);
  const [srcCanvasHeight, setSrcCanvasHeight] = useState(null);
  const [isChanges, setIsChanges] = useState(false);

  // console.log("isChanges: ", isChanges);

  const canvasRef = useRef(null);
  const unwarpedCanvasRef = useRef(null);

  const { observe, width, height } = useDimensions();

  useEffect(() => {
    if (!imgCanvas) {
      loadImage(sourceImgUrl, setImgCanvas);
    }
    // eslint-disable-next-line
  }, [sourceImgUrl]);

  useEffect(() => {
    if (!imgCanvas) return;

    const c = createOrientatedCanvas(imgCanvas, editData.orientation);
    setSourceCanvas(c);
    const displayCanvas = canvasRef.current;
    displayCanvas.width = c.width;
    displayCanvas.height = c.height;
    const ctx = displayCanvas.getContext("2d");
    ctx.drawImage(c, 0, 0);

    setSrcCanvasWidth(c.width);
    setSrcCanvasHeight(c.height);

    // eslint-disable-next-line
  }, [imgCanvas]);

  useEffect(() => {
    if (!sourceCanvas) return;

    const { width: w, height: h } = sourceCanvas;

    const { topLeft, topRight, bottomLeft, bottomRight } = cornerCoords;

    const a = { x: topLeft[0] * w, y: topLeft[1] * h };
    const b = { x: topRight[0] * w, y: topRight[1] * h };
    const c = { x: bottomRight[0] * w, y: bottomRight[1] * h };
    const d = { x: bottomLeft[0] * w, y: bottomLeft[1] * h };

    const drawSourceCanvas = ({ a, b, c, d }) => {
      const displayCanvas = canvasRef.current;
      const ctx = displayCanvas.getContext("2d");
      ctx.drawImage(sourceCanvas, 0, 0);
      ctx.strokeStyle = "rgba(255, 0, 255, 0.5)";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.lineTo(c.x, c.y);
      ctx.lineTo(d.x, d.y);
      ctx.closePath();
      ctx.stroke();
    };

    drawSourceCanvas({ a, b, c, d });
    setUnwarpedCanvas(getUnwarpedCanvas(sourceCanvas, { a, b, c, d }));
  }, [sourceCanvas, cornerCoords]);

  useEffect(() => {
    if (!unwarpedCanvas) return;

    const displayCanvas = unwarpedCanvasRef.current;
    displayCanvas.width = unwarpedCanvas.width;
    displayCanvas.height = unwarpedCanvas.height;
    const ctx = displayCanvas.getContext("2d");
    ctx.drawImage(unwarpedCanvas, 0, 0);
  }, [unwarpedCanvas]);

  const onSaveChanges = () => {
    onImageUpdate({
      canvas: unwarpedCanvas,
      editData: { ...editData, cornerCoords },
    });
  };

  const updateCorners = (newCorners) => {
    setIsChanges(true);
    setCornerCoords(newCorners);
  };

  return (
    <div className={styles.mythImageEditor}>
      <div className={styles.canvasAndCorners} ref={observe}>
        {srcCanvasWidth && srcCanvasHeight && (
          <Corners
            cornerCoords={cornerCoords}
            setCornerCoords={updateCorners}
            maxX={width}
            maxY={height}
          />
        )}
        <canvas ref={canvasRef} />
      </div>
      <canvas ref={unwarpedCanvasRef} />

      <div className={styles.controls}>
        <button
          onClick={onSaveChanges}
          disabled={isChanges === false}
          className="funButton"
        >
          Save
        </button>
        <button onClick={onCancel} className="funButton cancel">
          Cancel
        </button>
      </div>
    </div>
  );
}
